/**
 * Share quota bounds per billing model (plan Task 7.2; Learn "Azure Files scale targets", page dated 2026-05-20,
 * fetched 2026-09-25; Task 7.0 accepted 1–102,400 GiB on a pay-as-you-go StorageV2 account and refused 0 / 102,401
 * with 400 InvalidHeaderValue). On a provisioned account the quota IS the provisioned size (billed). Process-agnostic.
 */
import type { FileAccountFacts, FileShareBilling } from '../../types/azureStorage';

export const PAYG_QUOTA_MAX_GIB = 102_400;            // 100 TiB ("all pay-as-you-go file shares can grow up to 100 TiB")
export const PAYG_LEGACY_QUOTA_MAX_GIB = 5_120;       // an old account with large file shares explicitly disabled
export const PROVISIONED_V1_MIN_GIB = 100;
export const PROVISIONED_V1_MAX_GIB = 102_400;        // 100 TiB
export const PROVISIONED_V2_MIN_GIB = 32;
export const PROVISIONED_V2_MAX_GIB = 262_144;        // 256 TiB

export const QUOTA_LARGE_SHARES_UNKNOWN = 'The service decides whether quotas above 5 TiB are allowed on this account.';
export const QUOTA_PROVISIONED_VERSION_UNKNOWN = 'Jam SQL could not tell which provisioned model this account uses; the service decides the valid size.';
export const QUOTA_BILLING_UNKNOWN = 'Jam SQL could not tell how this account is billed; the service decides the valid size.';

export type ShareQuotaRange = { min: number; max: number; note?: string };

export function shareQuotaRange(facts: Pick<FileAccountFacts, 'billing' | 'largeFileShares'>): ShareQuotaRange {
  switch (facts.billing) {
    case 'pay-as-you-go':
      if (facts.largeFileShares === 'off') return { min: 1, max: PAYG_LEGACY_QUOTA_MAX_GIB };
      return facts.largeFileShares === 'on' ? { min: 1, max: PAYG_QUOTA_MAX_GIB } : { min: 1, max: PAYG_QUOTA_MAX_GIB, note: QUOTA_LARGE_SHARES_UNKNOWN };
    case 'provisioned-v1':
      return { min: PROVISIONED_V1_MIN_GIB, max: PROVISIONED_V1_MAX_GIB };
    case 'provisioned-v2':
      return { min: PROVISIONED_V2_MIN_GIB, max: PROVISIONED_V2_MAX_GIB };
    case 'provisioned':
      return { min: PROVISIONED_V2_MIN_GIB, max: PROVISIONED_V2_MAX_GIB, note: QUOTA_PROVISIONED_VERSION_UNKNOWN };
    case 'unknown':
      return { min: 1, max: PROVISIONED_V2_MAX_GIB, note: QUOTA_BILLING_UNKNOWN };
  }
}

/** The inline reason a quota is refused before any request, or undefined. */
export function quotaProblem(quotaGiB: number, facts: Pick<FileAccountFacts, 'billing' | 'largeFileShares'>): string | undefined {
  if (!Number.isInteger(quotaGiB)) return 'Enter the quota as a whole number of GiB.';
  const range = shareQuotaRange(facts);
  if (quotaGiB < range.min || quotaGiB > range.max) {
    return `Enter a quota from ${range.min.toLocaleString('en-US')} to ${range.max.toLocaleString('en-US')} GiB.${range.note ? ` ${range.note}` : ''}`;
  }
  return undefined;
}

/** What changing the quota means for the bill; undefined for pay-as-you-go (billed by use). */
export function billingSentence(billing: FileShareBilling): string | undefined {
  if (billing === 'pay-as-you-go') return undefined;
  if (billing === 'unknown') return 'If this account uses provisioned billing, the quota is the provisioned size and changing it changes what you pay.';
  return 'On this account the quota is the provisioned size; changing it changes what you pay.';
}
